/**
 * CustomerSidebar Component
 * 
 * Description: This component renders a sidebar for the customer panel. It includes navigation links to various sections of the customer interface, with corresponding icons and titles.
 * 
 * File Name: CustomerSidebar.tsx
 * Date: 06-08-2024
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SideBarNav from './SideBarNav';
import defaultProfile from "src/assets/images/default_profile_image.png";
import defaultBg from "src/assets/images/sidebar-bg.png";
import LinearProgress from '@mui/material/LinearProgress';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { customer, doctor, doctorMode } from 'src/lib/constans';
import Cookies from 'js-cookie';
import { truncateString } from 'src/helpers/helperFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';

const CustomerSidebar: React.FC = () => {
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const { profile } = useSelector((store: RootState) => store.doctor);
  const [mode, setMode] = useState(Cookies.get(doctorMode) || customer);
  const navigate = useNavigate()
  useEffect(() => {
    // Function to check for cookie changes
    const checkCookieChange = () => {
      const currentMode = Cookies.get(doctorMode) || customer;
      if (currentMode !== mode) {
        setMode(currentMode);
      }
    };

    // Set an interval to check for changes every second (1000ms)
    const intervalId = setInterval(checkCookieChange, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [mode]);
  const handleSwitchMode = () => {
    Cookies.set(doctorMode, mode === doctor ? customer : doctor);
    navigate("/dashboard");
  };
  return (
    <div id={'customer-sidebar-id'} className="customer-sidebar">
      <div className="parent-box customer-sidebar-first-box">
        <div className='cover-image-bg' style={{
          backgroundImage: `url(${userDetails?.backgroundPicture
            ?
            userDetails?.backgroundPicture
            :
            defaultBg
            })`
        }}></div>
        <div className="profile-info">
          <img
            alt='profile-image'
            className='rounded-full'
            style={{ border: "2px solid #fff" }}
            src={`${userDetails?.coverPhoto}?t=${Date.now()}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement; // Assert the type
              if (target) {
                target.src = defaultProfile;
              }
            }}
          />

          <div className="profile-des">
            <Link id='profile-name-link' to={'/profile-view'} className="flex flex-row gap-1 title-heading hover:underline font-Poppins-SemiBold font-size-20px capitalize">{`${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`}
              {userDetails?.userVerified === "verified" &&
                <span><FontAwesomeIcon icon={faCircleCheck} className="text-[#00443f]" /></span>
              }
            </Link>
            {userDetails?.userRole === doctor
              ?
              <div>
                <p className='font-size-14px font-Poppins-Regular line-clamp-3'>
                  {profile?.specailizedDetails?.map((v, i) => v.name || "").join(" | ")}
                </p>
                <p className='font-size-14px font-Poppins-Regular line-clamp-3 mt-2 theme-color-green'>
                  {profile?.educatuonHistory?.map((v, i) => v).join(", ")}
                </p>
              </div>
              :
              <span title={userDetails?.headLine || ""} className='font-Poppins-Regular text-[#707070] font-size-14px flex items-center justify-center'>{truncateString(userDetails?.headLine || "", 100)}</span>
            }
          </div>
        </div>
        <div className="profile-progress-box">
          <div className="progress-des">
            <p className='font-size-14px font-Poppins-Medium text-[#707070]'>Your profile</p>
            <p className="font-size-14px font-Poppins-SemiBold title-heading" >
              {Math.floor(userDetails?.profileCompletion || 0)}%</p>
          </div>
          <div className="progress-bar">
            <LinearProgress
              variant='determinate'
              value={Math.floor(userDetails?.profileCompletion || 0)}
              sx={{
                height: 5, // Change height here
                borderRadius: 5,
                backgroundColor: '#e0e0e0', // Background color
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#00443F', // Foreground color
                  borderRadius: 5,
                },
              }}
            />
          </div>
          <Link id='complete-profile-link' to={"/profile-view"} className="font-size-13px font-Poppins-SemiBold text-[#004540]">
            {Math.floor(userDetails?.profileCompletion || 0) >= 100 ? "Completed your profile" : "Complete your profile"}
          </Link>
        </div>
      </div>
      <div className="">
        <SideBarNav />
      </div>
    </div >
  );
};

export default CustomerSidebar;


// I am facing the following Problem:
// Avatar is basically hearing its own voice and taking it as input. For that you have to use the Voice Activity Detector, such that voice of doctor 